/**
 * Turning the ids the index facets on into names a person recognises.
 *
 * Algolia facets on ids — `departmentId:sample-nursing` — because names change
 * and ids do not. A dropdown of ids is useless, so names come from the
 * taxonomy callables, which any org member may read, and from the members
 * collection for owners.
 *
 * AN ID THAT CANNOT BE NAMED IS NOT OFFERED. The caller gets null, and the
 * dropdown drops that option; a dropdown with no options at all hides itself.
 * The alternative was live for one deploy and is worth recording: the Owner
 * filter listed "sample-user-000009 (2)" and "nYqsHJlPmogPaVzYqMFNr4VcRWU2
 * (1)". Nobody can act on that, and a filter nobody can act on is worse than
 * no filter, because it looks like the product is showing its plumbing.
 *
 * Losing an option is survivable: the policies behind it are still reachable
 * by typing, which is what the search box is for.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, getFirestore } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import { useAuth } from '../../auth/useAuth';
import { useTaxonomy } from '../admin/useTaxonomy';
import type { FacetField, SearchHit } from './types';

const db = getFirestore(app);

/** Statuses, as they should read on screen. */
export const STATUS_LABELS: Record<string, string> = {
  published: 'Published',
  approved: 'Approved',
  pending_review: 'In review',
  pending_approval: 'Awaiting approval',
  draft: 'Draft',
  archived: 'Archived',
  superseded: 'Superseded',
};

/** Null when the value cannot be named, and so should not be offered. */
export type LabelFor = (field: FacetField, value: string) => string | null;

/**
 * uid to display name.
 *
 * Read from Firestore rather than taken from the index, which stores
 * `ownerName: null` on a good many records. A role that cannot list members
 * gets an empty map and simply loses the Owner filter — the same graceful
 * failure the policy list makes for its Owner column.
 */
function useMemberNames(orgId: string | null) {
  return useQuery({
    queryKey: ['member-names', orgId],
    enabled: Boolean(orgId),
    staleTime: 300_000,
    queryFn: async (): Promise<Map<string, string>> => {
      const map = new Map<string, string>();
      try {
        const snap = await getDocs(collection(db, 'orgs', orgId!, 'members'));
        snap.docs.forEach((doc) => {
          const d = doc.data();
          const name = typeof d.displayName === 'string' && d.displayName ? d.displayName : d.email;
          if (typeof name === 'string' && name) map.set(doc.id, name);
        });
      } catch {
        // Not worth failing the page over; the filter hides instead.
      }
      return map;
    },
  });
}

export function useFacetLabels(hits: readonly SearchHit[], enabled: boolean): LabelFor {
  const { session } = useAuth();
  const departments = useTaxonomy('department', enabled);
  const categories = useTaxonomy('category', enabled);
  const documentTypes = useTaxonomy('documentType', enabled);
  const members = useMemberNames(enabled ? (session?.orgId ?? null) : null);

  /**
   * Names the hits themselves supply.
   *
   * Every record carries the snapshot name beside the id — `documentTypeId`
   * with `documentTypeName`, and so on — so the results in hand can name most
   * of the values their own facets report. This is not a nicety: the document
   * types in this data are not keyed by the ids the taxonomy callable returns,
   * so without it the Document type filter vanishes from a page whose results
   * are visibly labelled "Form" and "Procedure".
   */
  const fromHits = useMemo(() => {
    const dept = new Map<string, string>();
    const cat = new Map<string, string>();
    const type = new Map<string, string>();
    const owner = new Map<string, string>();

    for (const hit of hits) {
      if (hit.departmentId && hit.departmentName) dept.set(hit.departmentId, hit.departmentName);
      if (hit.categoryId && hit.categoryName) cat.set(hit.categoryId, hit.categoryName);
      if (hit.documentTypeId && hit.documentTypeName) {
        type.set(hit.documentTypeId, hit.documentTypeName);
      }
      // Usually empty — the index stores ownerName as null on most records,
      // which is why owners are read from the members collection instead.
      if (hit.ownerUid && hit.ownerName) owner.set(hit.ownerUid, hit.ownerName);
    }
    return { dept, cat, type, owner };
  }, [hits]);

  return useMemo(() => {
    // Taxonomy first, because it is complete; the hits fill what it misses.
    const merge = (canonical: [string, string][], fallback: Map<string, string>) =>
      new Map<string, string>([...fallback, ...canonical]);

    const byKind: Partial<Record<FacetField, Map<string, string>>> = {
      departmentId: merge((departments.data ?? []).map((d) => [d.id, d.name]), fromHits.dept),
      categoryId: merge((categories.data ?? []).map((c) => [c.id, c.name]), fromHits.cat),
      documentTypeId: merge(
        (documentTypes.data ?? []).map((t) => [t.id, t.name]),
        fromHits.type,
      ),
      ownerUid: merge([...(members.data ?? [])], fromHits.owner),
    };

    return (field: FacetField, value: string): string | null => {
      // Statuses are a closed set this app defines, so an unknown one is a
      // backend change worth surfacing rather than hiding.
      if (field === 'status') return STATUS_LABELS[value] ?? value;
      return byKind[field]?.get(value) ?? null;
    };
  }, [departments.data, categories.data, documentTypes.data, members.data, fromHits]);
}
